import { getStores, hasSDK } from '@littlebird/sdk';
import { computed, makeAutoObservable, reaction } from 'mobx';

import { getFeaturePayload, PAYLOAD_FEATURES } from '@/features/feature-flags';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import {
  type GhostChat,
  type GhostChatReveal,
  ghostRevealSchedule,
  parseGhostChatsPayload,
} from './ghost-chats';

const CLICKED_GHOST_CHATS_STORAGE_PREFIX = 'ghost-chats-clicked';

const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export type GhostChatSidebarItem = {
  id: string;
  type: 'ghost';
  section: 'recents';
  ghost: GhostChat;
};

function clickedStorageKey(userId: string): string {
  return `${CLICKED_GHOST_CHATS_STORAGE_PREFIX}:${userId}`;
}

function readClickedIds(userId: string | undefined): string[] {
  if (!userId) return [];
  const stored = getLocalStorageItem(clickedStorageKey(userId));
  return Array.isArray(stored)
    ? stored.filter((id): id is string => typeof id === 'string')
    : [];
}

function threadsLoaded(): boolean {
  return hasSDK() && getStores().threads.isLoaded;
}

class GhostChatsState {
  clickedIds: string[] = [];
  private nowMs = Date.now();
  private setGeneratedAt = new Date().toISOString();
  private revealTimer?: number;
  private shownPositions = new Map<string, number>();
  private renderedGhosts = new Map<string, GhostChat>();

  constructor() {
    makeAutoObservable(
      this,
      { visibleGhosts: computed.struct, sidebarItems: computed.struct },
      { autoBind: true }
    );

    reaction(() => userStore.user?.id, this.resetForUser, {
      fireImmediately: true,
    });
    reaction(() => this.nextRevealAtMs, this.scheduleNextReveal, {
      fireImmediately: true,
    });
    reaction(() => this.setIdentity, this.resetRecommendationSet);
  }

  private resetForUser(userId: string | undefined) {
    this.clickedIds = readClickedIds(userId);
  }

  private resetRecommendationSet() {
    this.shownPositions.clear();
    this.setGeneratedAt = new Date().toISOString();
    this.renderedGhosts.forEach(this.trackShown);
  }

  private get setIdentity(): string {
    return JSON.stringify([userStore.user?.id ?? null, this.ghosts]);
  }

  private get isActive(): boolean {
    return userStore.user?.is_welcome_page_eligible === true && threadsLoaded();
  }

  private get ghosts(): GhostChat[] {
    return parseGhostChatsPayload(
      getFeaturePayload(PAYLOAD_FEATURES.SIDEBAR_GHOST_CHATS)
    );
  }

  private get schedule(): GhostChatReveal[] {
    return ghostRevealSchedule({
      ghosts: this.ghosts,
      accountCreatedAtMs: userStore.accountCreatedAtMs,
      clickedIds: this.clickedIds,
    });
  }

  get visibleGhosts(): GhostChat[] {
    if (!this.isActive) return [];
    return this.schedule
      .filter((reveal) => reveal.revealAtMs <= this.nowMs)
      .map((reveal) => reveal.ghost);
  }

  get sidebarItems(): GhostChatSidebarItem[] {
    return this.visibleGhosts.map((ghost) => ({
      id: `ghost-${ghost.id}`,
      type: 'ghost',
      section: 'recents',
      ghost,
    }));
  }

  private get nextRevealAtMs(): number | null {
    if (!this.isActive) return null;
    const pending = this.schedule.find(
      (reveal) => reveal.revealAtMs > this.nowMs
    );
    return pending?.revealAtMs ?? null;
  }

  private advanceClock() {
    this.nowMs = Date.now();
    this.scheduleNextReveal();
  }

  private scheduleNextReveal() {
    window.clearTimeout(this.revealTimer);
    const revealAtMs = this.nextRevealAtMs;
    if (revealAtMs === null) return;

    const delayMs = Math.min(
      Math.max(1000, revealAtMs - Date.now()),
      MAX_TIMEOUT_MS
    );
    this.revealTimer = window.setTimeout(this.advanceClock, delayMs);
  }

  private trackGhostChat(
    event: 'RECOMMENDATION_SHOWN' | 'RECOMMENDATION_CLICKED',
    ghost: GhostChat,
    positionInList: number
  ) {
    trackEvent(event, {
      suggestion: ghost.prompt,
      ghost_chat_id: ghost.id,
      recommendation_set_generated_at: this.setGeneratedAt,
      container_position: 'sidebar',
      position_in_list: positionInList,
      size: 'sm',
    });
  }

  observeShown(ghost: GhostChat): () => void {
    this.renderedGhosts.set(ghost.id, ghost);
    this.trackShown(ghost);
    return () => this.stopObservingShown(ghost.id);
  }

  private stopObservingShown(ghostId: string) {
    this.renderedGhosts.delete(ghostId);
  }

  private trackShown(ghost: GhostChat) {
    if (this.shownPositions.has(ghost.id)) return;
    const positionInList =
      this.visibleGhosts.findIndex((visible) => visible.id === ghost.id) + 1;
    if (positionInList === 0) return;
    this.shownPositions.set(ghost.id, positionInList);
    this.trackGhostChat('RECOMMENDATION_SHOWN', ghost, positionInList);
  }

  markClicked(ghost: GhostChat) {
    if (this.clickedIds.includes(ghost.id)) return;
    this.trackGhostChat(
      'RECOMMENDATION_CLICKED',
      ghost,
      this.shownPositions.get(ghost.id) ?? 0
    );
    this.clickedIds = [...this.clickedIds, ghost.id];
    const userId = userStore.user?.id;
    if (userId) setLocalStorageItem(clickedStorageKey(userId), this.clickedIds);
  }
}

export const ghostChatsState = new GhostChatsState();
